import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  Mail,
  Lock,
  User,
  Phone,
  Eye,
  EyeOff,
  UserCheck,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { useViewport } from "../../hooks/useViewport";
import { UI, ui, buttonStyle, fieldStyles } from "../../utils/ui";
import Logo from "../../components/common/Logo";

const Register = () => {
  const { isDesktop } = useViewport();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    password: "",
    confirmPassword: "",
    user_type: "Select",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (formData.password !== formData.confirmPassword)
      return setError("Passwords do not match");
    if (formData.password.length < 8)
      return setError("Password must be at least 8 characters");
    setLoading(true);
    const { confirmPassword, ...userData } = formData;
    const result = await register(userData);
    if (result.success) {
      navigate(`/${result.user.user_type}/dashboard`);
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const benefits = [
    "Easy online booking",
    "Breathtaking Himalayan views",
    "Guided trekking experiences",
    "Cozy bonfire evenings",
  ];

  const spinner = (
    <span
      style={{
        width: 16,
        height: 16,
        border: "2px solid rgba(255,255,255,0.35)",
        borderTopColor: "#ffffff",
        borderRadius: "50%",
        animation: "spin 1s linear infinite",
      }}
    />
  );

  const inputRowStyle = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 12px",
    borderRadius: 14,
    border: `1.5px solid ${UI.colors.border}`,
    backgroundColor: "#ffffff",
  };

  const inputCoreStyle = {
    ...fieldStyles.input,
    border: "none",
    padding: 0,
    borderRadius: 0,
    backgroundColor: "transparent",
    flex: 1,
  };

  const linkButtonStyle = {
    border: "none",
    backgroundColor: "transparent",
    padding: 0,
    color: UI.colors.primary,
    fontWeight: 800,
    cursor: "pointer",
    textDecoration: "underline",
    textUnderlineOffset: 3,
  };

  return (
    <div style={{ ...ui.page, display: "flex" }}>
      {/* Left — Branding */}
      {isDesktop && (
        <div
          style={{
            width: "41.67%",
            position: "relative",
            backgroundImage:
              "url(https://images.unsplash.com/photo-1464207687429-7505649dae38?w=1200&h=1600&fit=crop)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(135deg, rgba(22,163,74,0.92) 0%, rgba(21,128,61,0.96) 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                textAlign: "center",
                color: "#ffffff",
                padding: "0 40px",
                maxWidth: 460,
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  margin: "0 auto 24px",
                  animation: "float 4s ease-in-out infinite",
                }}
              >
                <Logo
                  height={104}
                  onDark
                  showText={false}
                  style={{ padding: "16px 22px", borderRadius: 20 }}
                />
              </div>
              <h1
                style={{
                  fontFamily: UI.font.heading,
                  fontSize: 46,
                  fontWeight: 900,
                  margin: "0 0 8px",
                }}
              >
                Join Burans Hikes
              </h1>
              <p
                style={{
                  margin: 0,
                  color: "rgba(255,255,255,0.82)",
                  textTransform: "uppercase",
                  letterSpacing: 4,
                  fontSize: 12,
                }}
              >
                Start your journey
              </p>
              <div
                style={{
                  width: 64,
                  height: 1,
                  backgroundColor: "rgba(255,255,255,0.35)",
                  margin: "28px auto",
                }}
              />
              <p
                style={{
                  margin: "0 0 28px",
                  color: "rgba(255,255,255,0.86)",
                  fontSize: 16,
                  lineHeight: 1.7,
                }}
              >
                Create your account and discover the beauty of the Himalayas.
              </p>
              <div
                style={{
                  textAlign: "left",
                  display: "flex",
                  flexDirection: "column",
                  gap: 12,
                }}
              >
                {benefits.map((b) => (
                  <div
                    key={b}
                    style={{ display: "flex", alignItems: "center", gap: 10 }}
                  >
                    <CheckCircle2 size={18} color="rgba(255,255,255,0.9)" />
                    <span
                      style={{ color: "rgba(255,255,255,0.9)", fontSize: 13 }}
                    >
                      {b}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Right — Form */}
      <div
        style={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
      >
        <div style={{ width: "100%", maxWidth: 480, padding: "12px 0" }}>
          {/* Mobile Logo */}
          {!isDesktop && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 12,
                marginBottom: 24,
              }}
            >
              <Logo height={48} />
            </div>
          )}

          <div
            style={{
              ...ui.card,
              borderRadius: 20,
              boxShadow: "0 25px 50px rgba(0,0,0,0.12)",
            }}
          >
            <div style={{ padding: 32 }}>
              <div style={{ textAlign: "center", marginBottom: 22 }}>
                <h2
                  style={{
                    fontFamily: UI.font.heading,
                    fontSize: 28,
                    fontWeight: 900,
                    color: UI.colors.text,
                    margin: 0,
                  }}
                >
                  Create Account
                </h2>
                <p
                  style={{
                    color: UI.colors.muted,
                    margin: "10px auto 0",
                    fontSize: 14,
                  }}
                >
                  Sign up to start your mountain adventure
                </p>
              </div>

              {error && (
                <div
                  style={{
                    backgroundColor: "#fef2f2",
                    border: "1px solid #fecaca",
                    borderRadius: 14,
                    padding: 12,
                    marginBottom: 16,
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                  }}
                >
                  <AlertCircle size={18} color={UI.colors.danger} />
                  <span
                    style={{ fontSize: 13, color: "#991b1b", fontWeight: 700 }}
                  >
                    {error}
                  </span>
                </div>
              )}

              <form
                onSubmit={handleSubmit}
                style={{ display: "flex", flexDirection: "column", gap: 14 }}
              >
                {/* Full Name */}
                <div>
                  <label style={fieldStyles.label}>Full Name</label>
                  <div className="input-row" style={inputRowStyle}>
                    <User size={16} color="#9ca3af" />
                    <input
                      type="text"
                      name="name"
                      placeholder="Enter your full name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      style={inputCoreStyle}
                    />
                  </div>
                </div>

                {/* Email */}
                <div>
                  <label style={fieldStyles.label}>Email Address</label>
                  <div className="input-row" style={inputRowStyle}>
                    <Mail size={16} color="#9ca3af" />
                    <input
                      type="email"
                      name="email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      style={inputCoreStyle}
                    />
                  </div>
                </div>

                {/* Phone */}
                <div>
                  <label style={fieldStyles.label}>Phone Number</label>
                  <div className="input-row" style={inputRowStyle}>
                    <Phone size={16} color="#9ca3af" />
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Enter your phone number"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      style={inputCoreStyle}
                    />
                  </div>
                </div>

                {/* Account Type */}
                <div>
                  <label style={fieldStyles.label}>Account Type</label>
                  <div className="input-row" style={inputRowStyle}>
                    <UserCheck size={16} color="#9ca3af" />
                    <select
                      name="user_type"
                      value={formData.user_type}
                      onChange={handleChange}
                      required
                      style={{ ...inputCoreStyle, appearance: "none" }}
                    >
                      <option value="Select">Select</option>
                      <option value="customer">Customer — Book rooms</option>
                      <option value="agent">
                        Booking Agent — Manage bookings
                      </option>
                    </select>
                  </div>
                </div>

                {/* Password */}
                <div>
                  <label style={fieldStyles.label}>Password</label>
                  <div className="input-row" style={inputRowStyle}>
                    <Lock size={16} color="#9ca3af" />
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Min. 8 characters"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      style={inputCoreStyle}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      style={{
                        border: "none",
                        backgroundColor: "transparent",
                        cursor: "pointer",
                        padding: 4,
                        color: "#9ca3af",
                        display: "flex",
                      }}
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {/* Confirm Password */}
                <div>
                  <label style={fieldStyles.label}>Confirm Password</label>
                  <div className="input-row" style={inputRowStyle}>
                    <Lock size={16} color="#9ca3af" />
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      placeholder="Confirm your password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      required
                      style={inputCoreStyle}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      style={{
                        border: "none",
                        backgroundColor: "transparent",
                        cursor: "pointer",
                        padding: 4,
                        color: "#9ca3af",
                        display: "flex",
                      }}
                      aria-label={
                        showConfirmPassword
                          ? "Hide confirm password"
                          : "Show confirm password"
                      }
                    >
                      {showConfirmPassword ? (
                        <EyeOff size={16} />
                      ) : (
                        <Eye size={16} />
                      )}
                    </button>
                  </div>
                  <div style={fieldStyles.help}>Use at least 8 characters.</div>
                </div>

                {/* Terms */}
                {/* <label
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "flex-start",
                    cursor: "pointer",
                    marginTop: 4,
                  }}
                >
                  <input
                    type="checkbox"
                    required
                    style={{ marginTop: 2, accentColor: UI.colors.primary }}
                  />
                  <span
                    style={{
                      fontSize: 12,
                      color: UI.colors.muted,
                      lineHeight: 1.6,
                    }}
                  >
                    I agree to the{" "}
                    <button
                      type="button"
                      style={linkButtonStyle}
                      onClick={() => {}}
                    >
                      Terms &amp; Conditions
                    </button>{" "}
                    and{" "}
                    <button
                      type="button"
                      style={linkButtonStyle}
                      onClick={() => {}}
                    >
                      Privacy Policy
                    </button>
                    .
                  </span>
                </label> */}

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    ...buttonStyle("primary"),
                    width: "100%",
                    padding: "12px 16px",
                    borderRadius: 14,
                    marginTop: 4,
                    opacity: loading ? 0.75 : 1,
                  }}
                >
                  {loading ? spinner : null}
                  {loading ? "Creating Account..." : "Create Account"}
                </button>
              </form>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 12,
                  margin: "18px 0 10px",
                }}
              >
                <div
                  style={{
                    flex: 1,
                    height: 1,
                    backgroundColor: UI.colors.border,
                  }}
                />
                <span
                  style={{
                    fontSize: 12,
                    color: "#9ca3af",
                    fontWeight: 800,
                    letterSpacing: 2,
                    textTransform: "uppercase",
                  }}
                >
                  or
                </span>
                <div
                  style={{
                    flex: 1,
                    height: 1,
                    backgroundColor: UI.colors.border,
                  }}
                />
              </div>

              <p
                style={{
                  textAlign: "center",
                  fontSize: 13,
                  color: UI.colors.muted,
                  margin: 0,
                }}
              >
                Already have an account?{" "}
                <Link
                  to="/login"
                  style={{
                    color: UI.colors.primary,
                    fontWeight: 900,
                    textDecoration: "none",
                  }}
                >
                  Sign In
                </Link>
              </p>

              <div style={{ textAlign: "center", marginTop: 14 }}>
                <Link
                  to="/"
                  style={{
                    fontSize: 13,
                    color: "#9ca3af",
                    fontWeight: 800,
                    textDecoration: "none",
                  }}
                >
                  ← Back to Home
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
